import { useEffect, useState } from "react";

/**
 * Subtle right-edge table-of-contents rail for /marketing on desktop only.
 * - 1px gold vertical line, fixed right-8, vertically centered
 * - One dot per section with hover label (gold italic)
 * - Active dot glows gold; passed dots small + gold; future dots faint
 * - Click → smooth scroll to anchor id
 * - Hidden below 1024px (lg breakpoint)
 *
 * Pass `sections` as `[{id: "mk-anchor-...", label: "..."}, ...]`.
 */
export default function TocRail({ sections }) {
  const [activeId, setActiveId] = useState(sections[0]?.id || "");

  useEffect(() => {
    if (!sections.length) return;
    const els = sections
      .map((s) => ({ s, el: document.getElementById(s.id) }))
      .filter((x) => x.el);

    const onScroll = () => {
      // Active = topmost section whose top is <= 25% of viewport
      const threshold = window.innerHeight * 0.25;
      let current = els[0]?.s.id;
      for (const { s, el } of els) {
        const rect = el.getBoundingClientRect();
        if (rect.top - threshold <= 0) current = s.id;
      }
      setActiveId(current || els[0]?.s.id || "");
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [sections]);

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!sections.length) return null;
  const activeIndex = Math.max(0, sections.findIndex((s) => s.id === activeId));

  return (
    <nav
      data-testid="toc-rail"
      aria-label="Page sections"
      className="hidden lg:flex flex-col items-end gap-3 fixed right-8 top-1/2 -translate-y-1/2 z-40 select-none"
    >
      {sections.map((s, i) => {
        const isActive = s.id === activeId;
        const passed = i < activeIndex;
        return (
          <button
            key={s.id}
            onClick={() => jumpTo(s.id)}
            data-testid={`toc-dot-${s.id}`}
            className="group flex items-center gap-3 cursor-pointer focus:outline-none"
            aria-label={`Jump to ${s.label}`}
          >
            <span
              className={`overline italic font-serif-accent text-[#C9A050] opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap ${
                isActive ? "!opacity-90" : ""
              }`}
              style={{ letterSpacing: "0.08em", textTransform: "none", fontStyle: "italic" }}
            >
              {s.label}
            </span>
            <span
              className={`block rounded-full transition-all duration-500 ${
                isActive
                  ? "w-2.5 h-2.5 bg-[#C9A050] shadow-[0_0_0_4px_rgba(201,160,80,0.18)]"
                  : passed
                  ? "w-1.5 h-1.5 bg-[#C9A050]/70"
                  : "w-1 h-1 bg-white/25 group-hover:bg-[#C9A050]"
              }`}
            />
          </button>
        );
      })}
    </nav>
  );
}
